import { AwsUtil } from './../../authentication/aws.service';
import { CurrencyDisplayUtil } from './../../common/currency-display-util';
import { PollingStatus } from './../../service/polling-status';
import { ServiceRegistry } from './../../service/service-registry.service';
import { AppConfig } from './../../app.config';
import { MediaObserver } from '@angular/flex-layout';
import { MetricSelection } from './../../metrics/metric-selection';
import { OnDestroy } from '@angular/core';
import { SHLadderSelection } from './s-h-ladder-selection';
import { SHLadderConfig } from './s-h-ladder-config';
import { BucketingStrategy } from './../../bucket/bucketing-strategy';
import { AmChartsService } from '@amcharts/amcharts3-angular';
import { DashboardEventService } from './../../dashboard/dashboard-event.service';
import { Subscription } from 'rxjs';
import { SurvivalHorizonData, GetSurvivalHorizonServiceDataOutput } from '@liquitics/transaction';
import { SurvivalHorizonService } from './../survival-horizon.service';
import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import * as moment from 'moment';
import { LadderViewMode } from '../../home/home.sidebar.component';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-s-h-ladder',
    templateUrl: './s-h-ladder.component.html',
    styleUrls: ['./s-h-ladder.component.scss', './s-h-ladder-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SHLadderComponent implements OnInit, OnDestroy {
    DATA_FAILURE_COUNT_THRESHOLD = 5;
    POLLING_INTERVAL = 2500;

    asOfDateMode = false;
    compareMode = false;
    data: SurvivalHorizonData[];
    private bucketedDataByBlock: any = {};
    tableData: any = [];
    previousTableDataByBlockBucket = {};
    blocks: string[];
    chartBlocks: string[];
    amountCols: any[];

    selectedRow: SurvivalHorizonData;

    blockCharts = {};
    showChart = false;
    panelBlocked = false;
    private chartDataByBlock: any = {};

    private dataStreamDisposable: Subscription;

    private bucketing: BucketingStrategy;
    private configuration: SHLadderConfig;

    private initializedPostDataLoad = false;

    subscription: Subscription;

    constructor(protected survivalHorizonService: SurvivalHorizonService,
        protected appConfig: AppConfig,
        protected amCharts: AmChartsService,
        protected dashboardEventService: DashboardEventService,
        protected serviceRegistry: ServiceRegistry,
        protected awsUtil: AwsUtil,
        protected changeDetectorRef: ChangeDetectorRef,
        protected media: MediaObserver) {
        this.amountCols = [
            { field: 'inAmount', header: 'In' },
            { field: 'outAmount', header: 'Out' },
            { field: 'netAmount', header: 'Net' },
            { field: 'cumulativeAmount', header: 'Balance' },
            { field: 'bufferAmount', header: 'Buffer' },
            { field: 'liquiditySurplus', header: 'Surplus' }
        ];
    }

    protected initConfiguration(): SHLadderConfig {
        const configuration = new SHLadderConfig();
        configuration.blockKeyExtractor = (survivalHorizonData: SurvivalHorizonData) => {
            return survivalHorizonData.currency;
        };
        configuration.blockRetriever = () => {
            return this.appConfig.activeUserProfile.getCurrencies();
        };
        configuration.chartBlockRetriever = () => {
            return this.appConfig.activeUserProfile.getCurrencies();
        };
        configuration.liveStreaming = true;
        configuration.rowSelectionHandler = (selection: SHLadderSelection) => {
            this.dashboardEventService.ladderRowSelectionSubject.next(selection);
        };
        return configuration;
    }

    ngOnInit() {
        this.configuration = this.initConfiguration();
        this.blocks = this.configuration.blockRetriever();
        this.chartBlocks = this.configuration.chartBlockRetriever();
        this.subscription = this.dashboardEventService.ladderViewModeSubject.subscribe({
            next: mode => {
                this.toggleChartVisibility(mode);
            }
        });

        if (this.configuration.liveStreaming) {
            this.dataStreamDisposable = this.survivalHorizonService.getPollingDataSubject()
                .subscribe({
                    next: (serviceData: GetSurvivalHorizonServiceDataOutput) => {
                        this.handleServiceData(serviceData);
                    }, error: (err) => {
                        console.log(err);
                    }, complete:
                        () => {
                            console.log('Survival Horizon polling subscription completed.');
                            this.serviceRegistry.pollingStatus.next(PollingStatus.DISCONNECTED);
                        }
                });
        }
        this.subscription.add(this.dashboardEventService.ladderBucketingStrategySubject.subscribe({
            next: strategy => {
                this.changeBucketingStrategy(strategy);
                if (strategy != null && !this.initializedPostDataLoad) {
                    this.initializePostDataLoad();
                }
            }
        }));
        this.subscription.add(this.dashboardEventService.survivalHorizonModeSubject.subscribe({
            next: mode => {
                if (mode === 'fixed') {
                    this.refreshOnce(this.appConfig.asOfDatetimeUTC);
                } else {
                    this.asOfDateMode = false;
                    this.blocks = this.configuration.blockRetriever();
                    this.chartBlocks = this.configuration.chartBlockRetriever();
                }
            }
        }));
        this.subscription.add(this.media.media$.subscribe({
            next: change => {
                this.refreshCharts(true);
            }
        }));
        this.subscription.add(this.dashboardEventService.timeShiftSubject.subscribe({
            next: (newAsOfDatetime: moment.Moment) => {
                this.refreshOnce(newAsOfDatetime);
                this.asOfDateMode = this.appConfig.asOfDateMode;
                let existingStrategyName;
                if (this.bucketing) {
                    existingStrategyName = this.bucketing.strategyName();
                }
                this.bucketing = this.survivalHorizonService
                    .getBucketingStrategies()
                    .find((strat: BucketingStrategy) => {
                        return strat.strategyName() === existingStrategyName;
                    });
                if (!this.bucketing) {
                    this.bucketing = this.survivalHorizonService.getBucketingStrategies()[0];
                }
            }
        }));
        this.subscription.add(this.dashboardEventService.restartSubject.subscribe({
            next: (date: Date) => {
                const now = new Date();
                if (date > now) {
                    this.asOfDateMode = false;
                }
            }
        }));
    }

    protected refreshOnce(newAsOfDatetime: moment.Moment) {
        this.data = [];
        this.asOfDateMode = true;
        this.survivalHorizonService
            .getGetSurvivalHorizonServiceDataOutput()
            .pipe(take(1))
            .subscribe({
                next: (serviceData: GetSurvivalHorizonServiceDataOutput) => {
                    this.handleServiceData(serviceData, true);
                }, error: (err) => {
                    console.log(err);
                },
                complete: () => {
                    // Do something at end...
                }
            });

    }

    private handleServiceData(serviceData: GetSurvivalHorizonServiceDataOutput, isSnapshot?: boolean) {
        this.data = serviceData.positions;
        this.bucketAndShowLadderTableData(isSnapshot);
        if (this.showChart) {
            this.refreshCharts(false);
        }
        if (!this.initializedPostDataLoad) {
            this.initializePostDataLoad();
        }
    }

    private initializePostDataLoad() {
        if (this.tableData.length > 0 && this.bucketing != null) {
            // Auto-select the first row
            const selection = new SHLadderSelection();
            selection.data = this.tableData[0];
            this.selectedRow = selection.data;
            selection.selectedCurrency = this.blocks[0];
            selection.currencyIndex = 0;
            this.configuration.rowSelectionHandler(selection);
            this.initializedPostDataLoad = true;
        }
    }



    protected bucketAndShowLadderTableData(isSnapshot?: boolean) {
        if (!this.bucketing || !this.data || this.data.length === 0) {
            return;
        }
        const previousTableData = this.tableData;
        this.tableData = [];
        this.bucketedDataByBlock = {};
        this.chartDataByBlock = {};
        const today = this.bucketing.getToday();
        this.blocks.map(block => {
            const chartData = [];
            this.chartDataByBlock[block] = chartData;

            this.bucketedDataByBlock = this.survivalHorizonService.bucketDataByBlock(
                block,
                this.data,
                this.bucketing,
                this.bucketedDataByBlock,
                this.configuration.blockKeyExtractor,
                (entry: SurvivalHorizonData) => {
                    chartData.push(entry);
                });
        });
        const blockTableData = this.survivalHorizonService.bucketedBlockDataToTableData(this.bucketedDataByBlock)
            .map((row: any) => {
                row['trackByKey'] = this.generateRowTrackByKey(row);
                return row;
            });
        this.tableData = blockTableData;
        if (isSnapshot) {
            this.previousTableDataByBlockBucket = {};
            previousTableData.forEach((prev => {
                const key = prev.block + prev.bucket;
                this.previousTableDataByBlockBucket[key] = prev;
            }));
        } else {
            this.previousTableDataByBlockBucket = null;
        }
        this.changeDetectorRef.markForCheck();
    }

    round(value: number): number {
        return Math.round(value);
        // return value;
    }

    getColumnHeader(block: string, blockIndex: number) {
        return block.toUpperCase();
    }

    getSelectionMode() {
        return 'single';
    }


    tdDirectionalCellValue(directionalAddClass,
        directionalRemoveClass1,
        directionalRemoveClass2,
        ccyIndex,
        amountIndex,
        value,
        fieldName,
        rowData) {
        let changeClassToAdd = 'cell-same';
        let changeClassToRemove1 = 'cell-diff-up';
        let changeClassToRemove2 = 'cell-diff-down';
        if (this.previousTableDataByBlockBucket) {
            const prevRowData = this.previousTableDataByBlockBucket[rowData.block + rowData.bucket];
            if (prevRowData) {
                if (prevRowData[fieldName] > rowData[fieldName]) {
                    changeClassToRemove1 = 'cell-same';
                    changeClassToRemove2 = 'cell-diff-up';
                    changeClassToAdd = 'cell-diff-down';
                } else if (prevRowData[fieldName] < rowData[fieldName]) {
                    changeClassToRemove1 = 'cell-same';
                    changeClassToRemove2 = 'cell-diff-down';
                    changeClassToAdd = 'cell-diff-up';
                }
            }
        }
        return [
            [directionalAddClass, 'block_' + ccyIndex, 'amount_' + amountIndex, changeClassToAdd],
            [directionalRemoveClass1, directionalRemoveClass2, changeClassToRemove1, changeClassToRemove2]
        ];
    }


    tdPositiveCellValue(ccyIndex, amountIndex, value, fieldName, rowData) {
        return this.tdDirectionalCellValue('positive-cell', 'negative-cell', 'zero-cell', ccyIndex, amountIndex, value, fieldName, rowData);
    }
    tdNegativeCellValue(ccyIndex, amountIndex, value, fieldName, rowData) {
        return this.tdDirectionalCellValue('negative-cell', 'positive-cell', 'zero-cell', ccyIndex, amountIndex, value, fieldName, rowData);
    }
    tdZeroCellValue(ccyIndex, amountIndex, value, fieldName, rowData) {
        return this.tdDirectionalCellValue('zero-cell', 'positive-cell', 'negative-cell', ccyIndex, amountIndex, value, fieldName, rowData);
    }
    // tdPositiveCellValue(ccyIndex, amountIndex, value, fieldName, rowData) {
    //     let changeClassToAdd = 'cell-same';
    //     let changeClassToRemove1 = 'cell-diff-up';
    //     let changeClassToRemove2 = 'cell-diff-down';
    //     if (this.previousTableDataByBlockBucket) {
    //         const prevRowData = this.previousTableDataByBlockBucket[rowData.block + rowData.bucket];
    //         if (prevRowData) {
    //             if (prevRowData[fieldName] > rowData[fieldName]) {
    //                 changeClassToRemove1 = 'cell-same';
    //                 changeClassToRemove2 = 'cell-diff-up';
    //                 changeClassToAdd = 'cell-diff-down';
    //             } else if (prevRowData[fieldName] < rowData[fieldName]) {
    //                 changeClassToRemove1 = 'cell-same';
    //                 changeClassToRemove2 = 'cell-diff-down';
    //                 changeClassToAdd = 'cell-diff-up';
    //             }
    //         }
    //     }

    //     return [['positive-cell', 'block_' + ccyIndex, 'amount_' + amountIndex,
    // changeClassToAdd], ['negative-cell', 'zero-cell', changeClassToRemove1, changeClassToRemove2]];
    // }
    // tdNegativeCellValue(ccyIndex, amountIndex, value, fieldName, rowData) {
    //     return [['negative-cell', 'block_' + ccyIndex, 'amount_' + amountIndex], ['positive-cell', 'zero-cell']];
    // }
    // tdZeroCellValue(ccyIndex, amountIndex, value) {
    //     return [['zero-cell', 'block_' + ccyIndex, 'amount_' + amountIndex], ['positive-cell', 'negative-cell']];
    // }

    isPositive(value) {
        return Number.parseFloat(value) >= 0.5;
    }
    isNegative(value) {
        return Number.parseFloat(value) <= -0.5;
    }

    isZero(value) {
        const n = Number.parseFloat(value);
        return n < 0.5 && n > -0.5;
    }

    ngOnDestroy() {
        this.changeDetectorRef.detach();
        if (this.dataStreamDisposable) {
            this.dataStreamDisposable.unsubscribe();
        }
        this.subscription.unsubscribe();
    }
    flagIconClass(block) {
        const flagCode = CurrencyDisplayUtil.flagCodeForCurrency(block);
        if (!flagCode) {
            return '';
        }
        return 'flag-icon flag-icon-' + flagCode;
    }


    onRowSelect(event) {
        if (event.data.collapsed) {
            this.bucketing.handleBucketSelection(event.data.bucket);
            this.bucketAndShowLadderTableData();
        } else {
            const mouseEvent: MouseEvent = event.originalEvent;
            const selection = new SHLadderSelection();
            selection.data = event.data;
            selection.selectedCurrency = this.findBlockFromDOMClass(<Element>mouseEvent.srcElement, 0, 10);
            selection.currencyIndex = this.blocks.indexOf(selection.selectedCurrency);
            this.configuration.rowSelectionHandler(selection);
        }
    }

    private findBlockFromDOMClass(element: Element, currentDepth: number, maxDepth: number): string {
        if (currentDepth >= maxDepth) {
            // Worse case assume the first block (as in when the bucket column is selected)
            return this.blocks[0];
        }
        const className = element.className;
        const strIndex = className.indexOf('block_');
        if (strIndex > 0) {
            const blkIndex = +className.substring(strIndex + 6, strIndex + 7);
            if (blkIndex >= 0 && blkIndex < this.blocks.length) {
                return this.blocks[blkIndex];
            }
        }
        return this.findBlockFromDOMClass(element.parentElement, currentDepth + 1, maxDepth);
    }

    rowTrackBy(index: number, row: any) {
        return row['trackByKey'];
    }

    private generateRowTrackByKey(row: any) {
        let key = row.rowKey + row.bucket + row.bucketStart + row.collapsed;
        if (this.bucketing) {
            key += this.bucketing.strategyName();
        }
        if (this.blocks) {
            this.blocks.forEach((block: string) => {
                const columnPrefix = block + '_';
                this.amountCols.forEach((amountCol: any) => {
                    key += row[columnPrefix + amountCol.field];
                    key += '|';
                });
            });
        }
        return key;
    }

    toggleChartVisibility(mode: string) {
        this.showChart = (mode === 'chart');
        if (this.blockCharts) {
            this.initializeCharts();
            Object.keys(this.blockCharts).forEach(key => {
                this.blockCharts[key].invalidateSize();
            });
        }
        this.changeDetectorRef.detectChanges();
    }

    initializeCharts() {
        this.blocks.map(block => {
            this.createChart(block);
        });
        this.refreshCharts(true);
    }

    refreshCharts(invalidateSize: boolean) {
        this.blocks.map(block => {
            let chartData: SurvivalHorizonData[] = this.chartDataByBlock[block] || [];
            chartData = chartData.sort((d1, d2) => {
                if (d1.bucket === d2.bucket) {
                    return 0;
                }
                return d1.bucket < d2.bucket ? -1 : 1;
            });
            const chart = this.blockCharts[block];
            if (chart) {
                chart.dataProvider = chartData;
                // "zoomed" will be called after data update so we need to ignore the next call for it (for preservation)
                chart.ignoreZoomed = true;
                chart.validateData();
                if (invalidateSize) {
                    chart.invalidateSize();
                }
            }
        });
    }

    changeBucketingStrategy(strategy: BucketingStrategy) {
        if (strategy && this.bucketing !== strategy) {
            this.bucketing = strategy;
            if (this.data) {
                this.bucketAndShowLadderTableData();
            }
        }
    }

    chartHeight(): string {
        return '350px';
    }

    getBucketClass(row: any): string {
        return (row['collapsed'] === true) ? 'collapsed-bucket' : 'expanded-bucket';
    }

    protected chartDivName() {
        return 'liveChartDiv';
    }

    onHistory(block: string, metric: string) {
        this.dashboardEventService.metricSelectionSubject.next(
            new MetricSelection('HISTORY', block, metric, metric)
        );
    }

    createChart(block: string): any {
        const chart = this.amCharts.makeChart(this.chartDivName() + block, {
            'type': 'serial',
            'hideCredits': true,
            'theme': 'light',
            'addClassNames': true,
            'classNamePrefix': 'ladder',
            'dataDateFormat': 'YYYY-MM-DD',
            'precision': 2,
            'responsive': {
                'enabled': true,
                'rules': [
                    {
                        'minWidth': 250,
                        'overrides': {
                            'legend': {
                                'enabled': true
                            }
                        }
                    },
                ],
            },
            'valueAxes': [{
                'id': 'v1',
                'title': 'Movements',
                'position': 'left',
                'autoGridCount': false,
                'labelFunction': function (value) {
                    return '$' + Math.round(value) + 'M';
                }
            }, {
                'id': 'v2',
                'title': 'Aggregates',
                'gridAlpha': 0,
                'position': 'right',
                'autoGridCount': false,
                'guides': [{
                    'angle': 0,
                    'color': '#E71414',
                    'fillAlpha': 0.60,
                    'fillColor': '#BB5151',
                    'id': 'Guide-1',
                    'lineAlpha': 0.75,
                    'lineThickness': 2.00,
                    'toAngle': 0,
                    'toValue': 0,
                    'value': 0,
                }
                ],
            }],
            'graphs': [{
                'id': 'g3',
                'valueAxis': 'v1',
                'lineColor': '#e1ede9',
                'fillColors': '#F75D59',
                'fillAlphas': 1.0,
                'type': 'column',
                'title': 'out',
                'valueField': 'outAmount',
                'clustered': false,
                'columnWidth': 0.7,
                'legendValueText': '$[[value]]M',
                'hidden': true,
                // "balloonText": "[[title]]<br /><b style='font-size: 130%'>$[[value]]M</b>"
            }, {
                'id': 'g4',
                'valueAxis': 'v1',
                'lineColor': '#62cf73',
                'fillColors': '#62cf73',
                'fillAlphas': 1.0,
                'type': 'column',
                'title': 'In',
                'valueField': 'inAmount',
                'clustered': false,
                'columnWidth': 0.3,
                'legendValueText': '$[[value]]M',
                'hidden': true,
                // "balloonText": "[[title]]<br /><b style='font-size: 130%'>$[[value]]M</b>"
            }, {
                'id': 'g1',
                'valueAxis': 'v2',
                'bullet': 'round',
                'bulletBorderAlpha': 1,
                'bulletColor': '#FFFFFF',
                'bulletSize': 5,
                'hideBulletsCount': 50,
                'lineThickness': 2,
                'lineColor': '#20acd4',
                'type': 'step',
                'title': 'Net',
                'useLineColorForBulletBorder': true,
                'valueField': 'netAmount',
                // "balloonText": "[[title]]<br /><b style='font-size: 130%'>[[value]]</b>"
            }, {
                'id': 'g2',
                'valueAxis': 'v2',
                'bullet': 'round',
                'bulletBorderAlpha': 1,
                'bulletColor': '#FFFFFF',
                'bulletSize': 5,
                'hideBulletsCount': 50,
                'lineThickness': 2,
                'lineColor': '#4B0082',
                'type': 'step',
                // 'dashLength': 5,
                'title': 'Balance',
                'useLineColorForBulletBorder': true,
                'valueField': 'cumulativeAmount',
                // "balloonText": "[[title]]<br /><b style='font-size: 130%'>[[value]]</b>"
            }, {
                'id': 'g5',
                'valueAxis': 'v2',
                'bullet': 'round',
                'bulletBorderAlpha': 1,
                'bulletColor': '#FFFFFF',
                'bulletSize': 5,
                'hideBulletsCount': 50,
                'lineThickness': 2,
                'lineColor': '#87AFC7',
                'type': 'step',
                // 'dashLength': 5,
                'title': 'Surplus',
                'useLineColorForBulletBorder': true,
                'valueField': 'liquiditySurplus',
                'hidden': true,
                // "balloonText": "[[title]]<br /><b style='font-size: 130%'>[[value]]</b>"
            }
            ],
            'chartScrollbar': {
                'graph': 'g1',
                'oppositeAxis': false,
                'offset': 30,
                'scrollbarHeight': 20,
                'backgroundAlpha': 0,
                'selectedBackgroundAlpha': 0.1,
                'selectedBackgroundColor': '#888888',
                'graphFillAlpha': 0,
                'graphLineAlpha': 0.5,
                'selectedGraphFillAlpha': 0,
                'selectedGraphLineAlpha': 1,
                'autoGridCount': true,
                'color': '#AAAAAA'
            },
            'chartCursor': {
                'pan': true,
                'valueBalloonsEnabled': false,
                'avoidBalloonOverlapping': true,
                'valueLineEnabled': true,
                'valueLineBalloonEnabled': true,
                'cursorAlpha': 0,
                'valueLineAlpha': 0.2
            },
            'categoryField': 'bucket',
            'categoryAxis': {
                'parseDates': true,
                // 'dashLength': 1,
                'minorGridEnabled': true
            },
            'legend': {
                'useGraphSettings': true,
                'position': 'top',
                'marginLeft': 5,
                'marginRight': 5,
                'equalWidths': false,
                'maxColumns': 5,
                'spacing': 5,
                'valueWidth': 40,
                'forceWidth': true,
                'autoMargins': false,
                'fontSize': 8,
                'markerSize': 12,
                'align': 'right'
            },
            // "balloon": {
            //     "borderThickness": 1,
            //     "shadowAlpha": 0
            // },
            'export': {
                'enabled': false
            },
            'listeners': [
                {
                    'event': 'init',
                    'method': (event) => {
                        event.chart.chartCursor.addListener('changed', (e => {
                            if (event.chart.dataProvider && event.chart.dataProvider[e.index]) {
                                event.chart.selectedData = event.chart.dataProvider[e.index];
                            }
                        }));
                        event.chart.chartDiv.onclick = (e => {
                            if (e.srcElement.nodeName === 'path') {
                                // Make sure this is a click on the chart and not using the navigation controls
                                if (event.chart.selectedData) {
                                    const selection = new SHLadderSelection();
                                    selection.data = event.chart.selectedData;
                                    selection.data.bucketStart = selection.data.bucket;
                                    selection.selectedCurrency = selection.data.currency;
                                    selection.currencyIndex = this.blocks.indexOf(selection.data.currency);
                                    this.configuration.rowSelectionHandler(selection);
                                }
                            }
                        });
                    }
                },
                {
                    'event': 'zoomed',
                    'method': (event) => {
                        // Hold onto zoom dates for data updates
                        if (event.chart.ignoreZoomed) {
                            event.chart.ignoreZoomed = false;
                            return;
                        }
                        event.chart.zoomStartDate = event.startDate;
                        event.chart.zoomEndDate = event.endDate;
                    }
                },
                {
                    'event': 'dataUpdated',
                    'method': (event) => {
                        // Restore zooming levels
                        if (event.chart.zoomStartDate && event.chart.zoomEndDate) {
                            event.chart.zoomToDates(event.chart.zoomStartDate, event.chart.zoomEndDate);
                        }
                    }
                }
            ],
        });
        chart.ignoreZoomed = false;

        const title = document.createElement('div');
        title.innerHTML = block.toUpperCase();
        title.className = 'legend-title';
        chart.legendDiv.appendChild(title);
        this.blockCharts[block] = chart;
        return chart;
    }
}
